/**
 * Toasts: a queue, a polite live region, and a timer that stops while somebody
 * is reading.
 *
 * Three decisions worth naming.
 *
 * **The queue is real, not a cap.** Six outcomes arriving together used to mean
 * six toasts stacked and six timers started at the same instant, so the last
 * three expired before they had ever been near the top of the stack and nobody
 * ever saw them. Here at most three are on screen, the rest wait, and a toast's
 * timer starts when it becomes visible rather than when it was raised.
 *
 * **Auto-dismiss pauses on hover and on focus.** A message that disappears
 * while it is being read is worse than no message, because the person now knows
 * something happened and cannot find out what. Hovering anywhere over the stack
 * pauses all of it, not just the one under the cursor, because someone reading
 * the third toast is reading the stack.
 *
 * **Polite, always, including for errors.** `assertive` interrupts whatever a
 * screen-reader user is in the middle of, and an outcome that is worth
 * interrupting for is worth a dialog rather than a transient that will be gone
 * in six seconds. What errors get instead is no timer at all.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertIcon, CheckIcon, CloseIcon, InfoIcon } from '@/components/icons/Icons'
import { Button } from '@/components/primitives'
import { ToastContext } from './toastContext'
import type { ToastApi, ToastOptions, ToastRecord, ToastTone } from './toastContext'
import styles from './Toast.module.css'

/**
 * Three. Enough that a bulk action reporting per-item outcomes reads as a
 * group; few enough that the stack never reaches the middle of the screen,
 * where it starts covering the table it is reporting on.
 */
const MAX_VISIBLE = 3

/**
 * Dwell times.
 *
 * Six seconds is roughly twice the time it takes to read a title and a short
 * description at an unhurried pace, which leaves room for noticing the toast in
 * the first place. Warnings get longer because they usually say something the
 * reader has to decide about.
 *
 * Errors get no timer. An error is the one outcome where "it vanished before I
 * read it" costs real work, and it is also the one a person is most likely to
 * want to copy out of the screen.
 */
const DURATION_BY_TONE: Record<ToastTone, number> = {
  info: 6000,
  success: 5000,
  warning: 9000,
  danger: 0,
}

/** Announced before the title, because the icon carrying it is aria-hidden. */
const TONE_PREFIX: Record<ToastTone, string> = {
  info: 'Note:',
  success: 'Done:',
  warning: 'Warning:',
  danger: 'Error:',
}

let nextId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<ToastRecord[]>([])

  const dismiss = useCallback((id: string) => {
    setQueue((current) => current.filter((toast) => toast.id !== id))
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      show(options: ToastOptions) {
        const tone = options.tone ?? 'info'
        nextId += 1
        const id = `toast-${nextId}`
        setQueue((current) => [
          ...current,
          { ...options, id, tone, duration: options.duration ?? DURATION_BY_TONE[tone] },
        ])
        return id
      },
      dismiss,
      clear() {
        setQueue([])
      },
    }),
    [dismiss],
  )

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastViewport queue={queue} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastViewport({
  queue,
  onDismiss,
}: {
  queue: readonly ToastRecord[]
  onDismiss(id: string): void
}) {
  const [host] = useState(() => {
    const element = document.createElement('div')
    // Marks it for `useBackgroundInert`: a modal must not make its own
    // "Saved" message unreadable behind it.
    element.setAttribute('data-overlay-host', '')
    return element
  })
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    document.body.appendChild(host)
    return () => {
      host.remove()
    }
  }, [host])

  const visible = queue.slice(0, MAX_VISIBLE)

  return createPortal(
    /*
     * Mounted for the life of the application, whether or not there is anything
     * in it. A live region created with its message already inside is regularly
     * never announced at all: the platform has to have been watching the node
     * before the change to report the change.
     */
    <div
      className={styles.viewport}
      role="status"
      aria-live="polite"
      /* `role="status"` implies `aria-atomic="true"`, which would make a fourth
         toast arriving re-announce all four from the top. */
      aria-atomic="false"
      aria-label="Notifications"
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {visible.map((toast) => (
        <ToastItem key={toast.id} toast={toast} paused={paused} onDismiss={onDismiss} />
      ))}
    </div>,
    host,
  )
}

const TONE_ICON = {
  info: InfoIcon,
  success: CheckIcon,
  warning: AlertIcon,
  danger: AlertIcon,
} as const

function ToastItem({
  toast,
  paused,
  onDismiss,
}: {
  toast: ToastRecord
  paused: boolean
  onDismiss(id: string): void
}) {
  /*
   * The countdown is held in a ref and decremented on pause, rather than a
   * timer being restarted from the top each time. Restarting means a toast the
   * cursor passes over three times never expires; keeping the remainder means
   * pausing buys reading time and nothing more.
   */
  const remaining = useRef(toast.duration)
  const { id, duration } = toast

  useEffect(() => {
    if (paused || duration === 0 || remaining.current <= 0) return
    const startedAt = Date.now()
    const timer = window.setTimeout(() => onDismiss(id), remaining.current)
    return () => {
      window.clearTimeout(timer)
      remaining.current -= Date.now() - startedAt
    }
  }, [paused, duration, id, onDismiss])

  const Icon = TONE_ICON[toast.tone]

  return (
    <div className={styles.toast} data-tone={toast.tone}>
      <span className={styles.icon} aria-hidden>
        <Icon size={18} />
      </span>

      <div className={styles.text}>
        <p className={styles.title}>
          {/* The tone is a colour and a shape on screen and neither reaches a
              screen reader, so it is also a word. */}
          <span className="visually-hidden">{TONE_PREFIX[toast.tone]} </span>
          {toast.title}
        </p>
        {toast.description ? <p className={styles.description}>{toast.description}</p> : null}
      </div>

      {toast.action ? (
        <Button variant="secondary" size="sm" onClick={toast.action.onClick}>
          {toast.action.label}
        </Button>
      ) : null}

      <Button
        variant="ghost"
        size="sm"
        iconOnly
        aria-label="Dismiss notification"
        onClick={() => onDismiss(id)}
      >
        <CloseIcon size={16} />
      </Button>
    </div>
  )
}
